/**
 * Timetable Helper Functions
 *
 * Shared utility functions for timetable operations.
 * Consolidates duplicate logic from DisciplineDashboard, StatsCalculator, etc.
 */

import { timetablesService } from '../services/timetablesService';
import { dayTemplatesService } from '../services/dayTemplatesService';
import { schoolPeriodsService } from '../services/schoolPeriodsService';

/**
 * Get the current period for a school at a given time
 * Uses timetable system if available, falls back to legacy periods
 *
 * @param {string} schoolId - The school ID
 * @param {Date} time - The time to check (defaults to now)
 * @returns {Promise<Object|null>} - Period object or null
 */
export async function getCurrentPeriod(schoolId, time = new Date()) {
  if (!schoolId) {
    return null;
  }

  try {
    // Try timetable-based lookup first
    const dayTemplate = await timetablesService.getCurrentDayTemplate(schoolId, time);
    if (dayTemplate) {
      return dayTemplatesService.getCurrentPeriod(dayTemplate, time);
    }

    // Fall back to legacy schoolPeriods
    const periods = await schoolPeriodsService.getAll(schoolId);
    return schoolPeriodsService.getCurrentPeriod(periods, time);
  } catch (error) {
    console.error('Error getting current period:', error);
    return null;
  }
}

/**
 * Get the current day template for a school
 *
 * @param {string} schoolId - The school ID
 * @param {Date} date - The date to check (defaults to today)
 * @returns {Promise<Object|null>} - Day template object or null
 */
export async function getCurrentDayTemplate(schoolId, date = new Date()) {
  if (!schoolId) {
    return null;
  }

  try {
    return await timetablesService.getCurrentDayTemplate(schoolId, date);
  } catch (error) {
    console.error('Error getting current day template:', error);
    return null;
  }
}

/**
 * Get all periods for a specific day (from timetable or legacy)
 *
 * @param {string} schoolId - The school ID
 * @param {Date} date - The date to get periods for
 * @returns {Promise<Array>} - Array of period objects
 */
export async function getPeriodsForDay(schoolId, date = new Date(), { allowDefaults = true } = {}) {
  if (!schoolId) {
    return [];
  }

  try {
    // Try timetable-based lookup first
    const dayTemplate = await timetablesService.getCurrentDayTemplate(schoolId, date);
    if (dayTemplate && dayTemplate.periods) {
      return dayTemplate.periods;
    }

    // Fall back to legacy schoolPeriods
    return await schoolPeriodsService.getAll(schoolId, { allowDefaults });
  } catch (error) {
    console.error('Error getting periods for day:', error);
    return [];
  }
}

/**
 * Format time range for display (e.g., "8:30 AM - 9:20 AM")
 *
 * @param {string} startTime - Start time in HH:MM format
 * @param {string} endTime - End time in HH:MM format
 * @returns {string} - Formatted time range
 */
export function formatTimeRange(startTime, endTime) {
  const formatTime = (time) => {
    const [hour, minute] = time.split(':');
    const h = parseInt(hour);
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayHour = h > 12 ? h - 12 : h === 0 ? 12 : h;
    return `${displayHour}:${minute} ${ampm}`;
  };

  return `${formatTime(startTime)} - ${formatTime(endTime)}`;
}

/**
 * Calculate duration between two times in minutes
 *
 * @param {string} startTime - Start time in HH:MM format
 * @param {string} endTime - End time in HH:MM format
 * @returns {number} - Duration in minutes
 */
export function calculateDuration(startTime, endTime) {
  const [startHour, startMinute] = startTime.split(':').map(Number);
  const [endHour, endMinute] = endTime.split(':').map(Number);

  const startMinutes = startHour * 60 + startMinute;
  const endMinutes = endHour * 60 + endMinute;

  return endMinutes - startMinutes;
}

/**
 * Format duration in human-readable format (e.g., "1h 30m")
 *
 * @param {number} minutes - Duration in minutes
 * @returns {string} - Formatted duration
 */
export function formatDuration(minutes) {
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;

  if (hours > 0 && mins > 0) {
    return `${hours}h ${mins}m`;
  } else if (hours > 0) {
    return `${hours}h`;
  } else {
    return `${mins}m`;
  }
}

/**
 * Check if a time is within a period
 *
 * @param {Date} time - The time to check
 * @param {Object} period - Period object with startTime and endTime
 * @returns {boolean} - True if time is within period
 */
export function isTimeInPeriod(time, period) {
  const hours = time.getHours();
  const minutes = time.getMinutes();
  const currentMinutes = hours * 60 + minutes;

  const [startHour, startMinute] = period.startTime.split(':').map(Number);
  const [endHour, endMinute] = period.endTime.split(':').map(Number);

  const startMinutes = startHour * 60 + startMinute;
  const endMinutes = endHour * 60 + endMinute;

  return currentMinutes >= startMinutes && currentMinutes <= endMinutes;
}

/**
 * Get period by label from a list of periods
 *
 * @param {Array} periods - Array of period objects
 * @param {string} label - Period label to find
 * @returns {Object|null} - Period object or null
 */
export function getPeriodByLabel(periods, label) {
  return periods.find(p => p.label === label) || null;
}

/**
 * Get next period after current time
 *
 * @param {Array} periods - Array of period objects (sorted by startTime)
 * @param {Date} time - The time to check (defaults to now)
 * @returns {Object|null} - Next period object or null
 */
export function getNextPeriod(periods, time = new Date()) {
  const hours = time.getHours();
  const minutes = time.getMinutes();
  const currentMinutes = hours * 60 + minutes;

  for (const period of periods) {
    const [startHour, startMinute] = period.startTime.split(':').map(Number);
    const startMinutes = startHour * 60 + startMinute;

    if (startMinutes > currentMinutes) {
      return period;
    }
  }

  return null;
}

/**
 * Get previous period before current time
 *
 * @param {Array} periods - Array of period objects (sorted by startTime)
 * @param {Date} time - The time to check (defaults to now)
 * @returns {Object|null} - Previous period object or null
 */
export function getPreviousPeriod(periods, time = new Date()) {
  const hours = time.getHours();
  const minutes = time.getMinutes();
  const currentMinutes = hours * 60 + minutes;

  let previousPeriod = null;

  for (const period of periods) {
    const [endHour, endMinute] = period.endTime.split(':').map(Number);
    const endMinutes = endHour * 60 + endMinute;

    if (endMinutes < currentMinutes) {
      previousPeriod = period;
    } else {
      break;
    }
  }

  return previousPeriod;
}

/**
 * Filter periods by type
 *
 * @param {Array} periods - Array of period objects
 * @param {string} type - Period type ('class', 'break', 'non-class', etc.)
 * @returns {Array} - Filtered periods
 */
export function getPeriodsByType(periods, type) {
  return periods.filter(p => p.type === type);
}

/**
 * Get school day start and end times
 *
 * @param {Array} periods - Array of period objects
 * @returns {Object} - {startTime, endTime}
 */
export function getSchoolDayBounds(periods) {
  if (!periods || periods.length === 0) {
    return { startTime: '08:00', endTime: '15:00' };
  }

  // Sort by start time
  const sorted = [...periods].sort((a, b) => {
    const [aHour, aMin] = a.startTime.split(':').map(Number);
    const [bHour, bMin] = b.startTime.split(':').map(Number);
    return (aHour * 60 + aMin) - (bHour * 60 + bMin);
  });

  return {
    startTime: sorted[0].startTime,
    endTime: sorted[sorted.length - 1].endTime
  };
}

/**
 * Validate time format (HH:MM)
 *
 * @param {string} time - Time string to validate
 * @returns {boolean} - True if valid
 */
export function isValidTime(time) {
  const regex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
  return regex.test(time);
}

/**
 * Check if two time periods overlap
 *
 * @param {Object} period1 - First period with startTime and endTime
 * @param {Object} period2 - Second period with startTime and endTime
 * @returns {boolean} - True if periods overlap
 */
export function doPeriodsOverlap(period1, period2) {
  const [start1Hour, start1Minute] = period1.startTime.split(':').map(Number);
  const [end1Hour, end1Minute] = period1.endTime.split(':').map(Number);
  const [start2Hour, start2Minute] = period2.startTime.split(':').map(Number);
  const [end2Hour, end2Minute] = period2.endTime.split(':').map(Number);

  const start1 = start1Hour * 60 + start1Minute;
  const end1 = end1Hour * 60 + end1Minute;
  const start2 = start2Hour * 60 + start2Minute;
  const end2 = end2Hour * 60 + end2Minute;

  return (start1 < end2 && end1 > start2) || (start2 < end1 && end2 > start1);
}

/**
 * Get cycle day name (e.g., "Day 1", "Day 2", etc.)
 *
 * @param {number} cycleDay - Zero-based cycle day index
 * @returns {string} - Human-readable cycle day name
 */
export function getCycleDayName(cycleDay) {
  return `Day ${cycleDay + 1}`;
}

/**
 * Format date as YYYY-MM-DD
 *
 * @param {Date} date - Date to format
 * @returns {string} - Formatted date string
 */
export function formatDateKey(date) {
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Parse date key (YYYY-MM-DD) to Date object
 *
 * @param {string} dateKey - Date key string
 * @returns {Date} - Date object
 */
export function parseDateKey(dateKey) {
  const [year, month, day] = dateKey.split('-').map(Number);
  return new Date(year, month - 1, day);
}

/**
 * Get timetable status summary for a school
 * Useful for dashboard displays
 *
 * @param {string} schoolId - The school ID
 * @param {Date} date - The date to check (defaults to today)
 * @returns {Promise<Object>} - Status object with current period, day template, etc.
 */
export async function getTimetableStatus(schoolId, date = new Date()) {
  if (!schoolId) {
    return null;
  }

  try {
    const [dayTemplate, currentPeriod, timetable] = await Promise.all([
      getCurrentDayTemplate(schoolId, date),
      getCurrentPeriod(schoolId, date),
      timetablesService.getActive(schoolId, date)
    ]);

    let cycleDayName = null;
    let cycleDay = null;

    if (timetable) {
      cycleDay = timetablesService.getCycleDay(date, timetable.startDate, timetable.cycleLength, timetable.daysOfWeek);
      cycleDayName = getCycleDayName(cycleDay);
    }

    return {
      dayTemplate,
      currentPeriod,
      timetable,
      cycleDay,
      cycleDayName,
      hasTimetable: !!timetable,
      isActive: !!currentPeriod
    };
  } catch (error) {
    console.error('Error getting timetable status:', error);
    return null;
  }
}

/**
 * Constants for period types
 */
export const PERIOD_TYPES = {
  CLASS: 'class',
  BREAK: 'break',
  NON_CLASS: 'non-class',
  ASSEMBLY: 'assembly',
  ADMIN: 'admin',
  READING: 'reading',
  TUTORIAL: 'tutorial',
  OTHER: 'other'
};

/**
 * Get display label for period type
 *
 * @param {string} type - Period type
 * @param {Object} [activityTypeMap] - Optional map of activity types by ID for dynamic lookup
 * @returns {string} - Display label
 */
export function getPeriodTypeLabel(type, activityTypeMap) {
  // Try dynamic lookup first
  if (activityTypeMap?.[type]) {
    return activityTypeMap[type].name;
  }

  const labels = {
    [PERIOD_TYPES.CLASS]: 'Class',
    [PERIOD_TYPES.BREAK]: 'Break',
    [PERIOD_TYPES.NON_CLASS]: 'Non-Class',
    [PERIOD_TYPES.ASSEMBLY]: 'Assembly',
    [PERIOD_TYPES.ADMIN]: 'Admin',
    [PERIOD_TYPES.READING]: 'Reading',
    [PERIOD_TYPES.TUTORIAL]: 'Tutorial',
    [PERIOD_TYPES.OTHER]: 'Other'
  };

  return labels[type] || type;
}

/**
 * Get CSS class for period type badge
 *
 * @param {string} type - Period type
 * @returns {string} - DaisyUI badge class
 */
export function getPeriodTypeBadgeClass(type) {
  const classes = {
    [PERIOD_TYPES.CLASS]: 'badge-primary',
    [PERIOD_TYPES.BREAK]: 'badge-secondary',
    [PERIOD_TYPES.NON_CLASS]: 'badge-neutral',
    [PERIOD_TYPES.ASSEMBLY]: 'badge-accent',
    [PERIOD_TYPES.ADMIN]: 'badge-info',
    [PERIOD_TYPES.READING]: 'badge-success',
    [PERIOD_TYPES.TUTORIAL]: 'badge-warning',
    [PERIOD_TYPES.OTHER]: 'badge-ghost'
  };

  return classes[type] || 'badge-ghost';
}

/**
 * Get semantic color class for period type (left border strip)
 */
export function getPeriodTypeColorClass(type) {
  const colors = {
    [PERIOD_TYPES.CLASS]: 'bg-primary',
    [PERIOD_TYPES.BREAK]: 'bg-secondary',
    [PERIOD_TYPES.NON_CLASS]: 'bg-neutral',
    [PERIOD_TYPES.ASSEMBLY]: 'bg-accent',
    [PERIOD_TYPES.ADMIN]: 'bg-info',
    [PERIOD_TYPES.READING]: 'bg-success',
    [PERIOD_TYPES.TUTORIAL]: 'bg-warning',
    [PERIOD_TYPES.OTHER]: 'bg-base-300'
  };
  return colors[type] || 'bg-base-300';
}

/**
 * Get display label for a period object.
 * Uses periodNumber when available (numbered periods get "Period X"),
 * otherwise falls back to label, then type label.
 *
 * @param {Object} period - Period object from day template
 * @returns {string} - Display label
 */
export function getPeriodDisplayLabel(period) {
  if (period.periodNumber) {
    return `Period ${period.periodNumber}`;
  }
  return period.label || getPeriodTypeLabel(period.type);
}

/**
 * Whether a period type should be numbered by default
 *
 * @param {string} type - Period type
 * @param {Object} [activityTypeMap] - Optional map for dynamic lookup
 * @returns {boolean}
 */
export function isNumberedType(type, activityTypeMap) {
  if (type === PERIOD_TYPES.CLASS) return true;
  // Legacy tutorial numbering
  if (type === PERIOD_TYPES.TUTORIAL) return true;
  // Activity types are not numbered
  if (activityTypeMap?.[type]) return false;
  return false;
}

/**
 * Add minutes to a time string (HH:MM)
 */
export function addMinutesToTime(timeStr, minutes) {
  const [h, m] = timeStr.split(':').map(Number);
  const totalMinutes = h * 60 + m + minutes;
  const newH = Math.floor(totalMinutes / 60) % 24;
  const newM = totalMinutes % 60;
  return `${String(newH).padStart(2, '0')}:${String(newM).padStart(2, '0')}`;
}
